/**
 * Account type utilities
 * 
 * Helpers for handling differences between Futures and Margin/Equity accounts.
 * See src/mock_data/accountDataExamples.js for detailed data structure differences.
 */

import { isConnectTradeAccount } from './brokerConnection';

/**
 * Check if account is futures type
 */
export const isFuturesAccount = (balance) => {
  return balance?.AccountType === 'Futures';
};

/**
 * Live futures day P&L (realized + open, adjusted by the balance snapshot)
 * matches TradeStation Direct, whose futures day figure only moves on balance polls.
 * Connect Trade futures stream their own day P&L and must not use that formula.
 */
export const isDirectTradestationFuturesAccount = (account, balance) => {
  const accountType = balance?.AccountType || account?.AccountType;
  if (accountType !== 'Futures') return false;
  return !isConnectTradeAccount(account);
};

/**
 * Check if a position has today's profit/loss data
 * Futures positions do NOT have this field; margin/equity positions DO
 */
export const positionHasTodaysPL = (position) => {
  return position?.TodaysProfitLoss !== undefined;
};

/**
 * Get Today's Change based on account type:
 * - Futures: Use balance.TodaysProfitLoss (includes both realized and unrealized)
 * - Equity/Margin: Use Σ(position.TodaysProfitLoss) ONLY (change in open positions)
 * 
 * Note: For equity accounts, we exclude realized P&L from closed trades to show
 * only the intraday change of currently open positions. Closed trades are tracked
 * separately in the journal/history.
 * 
 * @param {Object} balance - The balance object for the account
 * @param {Array} positions - Array of position objects for the account
 * @returns {number} Today's change value
 */
export const calculateTodaysChange = (balance, positions) => {
  if (!balance) return 0;
  
  const isFutures = isFuturesAccount(balance);
  
  if (isFutures) {
    // Futures: Use balance's TodaysProfitLoss (positions don't have TodaysProfitLoss field)
    return parseFloat(balance.TodaysProfitLoss) || 0;
  } else {
    // Equity/Margin: Sum of position TodaysProfitLoss ONLY (no realized P&L from closed trades)
    // This shows the intraday change of currently open positions
    const positionsTodayPL = (positions || []).reduce((sum, pos) => {
      return sum + (parseFloat(pos.TodaysProfitLoss) || 0);
    }, 0);
    return positionsTodayPL;
  }
};

function finiteOrNull(value) {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Futures today's P&L that moves with the position stream.
 *
 * Balance `TodaysProfitLoss` only changes when balances are polled, so the day
 * figure sat still while open P&L ticked. Beginning-of-day equity is
 * equity − realized − the balance snapshot's open P&L. Live equity is that
 * base plus realized P&L plus open P&L from positions, so:
 *
 *   today's P&L = realized + live open + (balance today unrealized − balance open)
 *
 * The parenthetical is the overnight gap already in the broker snapshot. For a
 * position opened today it is ~0, and today's P&L is realized plus live open P&L.
 * Between polls the day number moves 1:1 with open P&L.
 */
export function futuresLiveTodayPnL({
  realizedPL = 0,
  balanceTodayPL = null,
  balanceUnrealizedPL = null,
  liveOpenPL = 0,
} = {}) {
  const realized = finiteOrNull(realizedPL) ?? 0;
  const liveOpen = finiteOrNull(liveOpenPL) ?? 0;
  const balanceToday = finiteOrNull(balanceTodayPL);
  const balanceOpen = finiteOrNull(balanceUnrealizedPL);

  const todayUnrealized = balanceToday != null && balanceOpen != null
    ? liveOpen + (balanceToday - balanceOpen)
    : liveOpen;

  return {
    todayUnrealizedChange: todayUnrealized,
    todayChange: realized + todayUnrealized,
  };
}

/**
 * P&L once the position book is flat. Open P&L is 0 immediately — the balance
 * snapshot still has the closed trade's open figure until the next poll.
 * Futures today's number drops that open piece right away, then follows the
 * refetched balance. Equity day P&L is the open book, so it is 0 when flat.
 */
export function flatBookPnL({
  realizedPL = 0,
  balanceTodayPL = null,
  balanceUnrealizedPL = null,
  isDirectTradestationFutures = false,
  isFutures = false,
} = {}) {
  const realized = finiteOrNull(realizedPL) ?? 0;
  if (isDirectTradestationFutures || isFutures) {
    const live = futuresLiveTodayPnL({
      realizedPL: realized,
      balanceTodayPL,
      balanceUnrealizedPL,
      liveOpenPL: 0,
    });
    return {
      unrealizedPL: 0,
      todayUnrealizedChange: live.todayUnrealizedChange,
      todayChange: live.todayChange,
      realizedPL: realized,
    };
  }
  return {
    unrealizedPL: 0,
    todayUnrealizedChange: 0,
    todayChange: realized,
    realizedPL: realized,
  };
}

/**
 * TradeStation Direct REST balance poll intervals (only while positions are open).
 * Connect Trade should not use these — balances come from the brokerage WS topic.
 * - Futures: 1s (today's P&L lives on the balance object, not positions)
 * - Margin: 5s (position stream covers most P&L; balances for equity/BP)
 */
export const BALANCE_REFRESH_INTERVAL_FUTURES = 1000;
export const BALANCE_REFRESH_INTERVAL_MARGIN = 5000;

/**
 * @param {object} [opts]
 * @param {object} [opts.balance] - balance row (AccountType)
 * @param {object} [opts.account] - selected account (AccountID heuristic for futures)
 * @param {boolean} [opts.hasActivePositions]
 * @param {boolean} [opts.isConnectTrade] - CT streams balances; no REST poll
 * @returns {number|false} ms interval, or false to disable polling
 */
export const getBalanceRefreshInterval = ({
  balance,
  account,
  hasActivePositions = false,
  isConnectTrade = false,
} = {}) => {
  if (isConnectTrade) return false;
  if (!hasActivePositions) return false;
  if (isFuturesAccount(balance) || account?.AccountID?.endsWith('F')) {
    return BALANCE_REFRESH_INTERVAL_FUTURES;
  }
  return BALANCE_REFRESH_INTERVAL_MARGIN;
};














